import { isLgpdAtivo } from '@/services/lgpd.service';

function shouldMask(): boolean {
    return isLgpdAtivo();
}

function maskCpf(value: string): string {
    if (!shouldMask() || !value) return value;
    const apenasDigitos = value.replace(/\D/g, '');
    if (apenasDigitos.length !== 11) return value;
    const parte2 = apenasDigitos.substring(3, 6);
    const parte4 = apenasDigitos.substring(9, 11);
    return `***.${parte2}.***-${parte4}`;
}

function maskCnpj(value: string): string {
    if (!shouldMask() || !value) return value;
    const apenasDigitos = value.replace(/\D/g, '');
    if (apenasDigitos.length !== 14) return value;
    const parte2 = apenasDigitos.substring(2, 5);
    const parte4 = apenasDigitos.substring(8, 12);
    const parte5 = apenasDigitos.substring(12, 14);
    return `**.${parte2}.***/***${parte4.substring(2, 4)}-${parte5}`;
}

function maskNome(value: string): string {
    if (!shouldMask() || !value) return value;
    return value
        .split(' ')
        .map((n) => (n[0] ? `${n[0]}***` : ''))
        .join(' ');
}

function maskEmail(value: string): string {
    if (!shouldMask() || !value) return value;
    const [user, domain] = value.split('@');
    if (!domain) return value;
    const domainParts = domain.split('.');
    if (domainParts.length < 2) return value;
    const domainName = domainParts[0];
    const ext = domainParts.slice(1).join('.');
    return `${user[0]}***@${domainName[0]}***.${ext}`;
}

function maskTextoGenerico(value: string): string {
    if (!value || value.length <= 3) return '***';
    if (value.length <= 5) return `${value[0]}***`;
    return `${value[0]}***${value[value.length - 1]}`;
}

function detectarTipo(value: string): string {
    if (!value) return 'desconhecido';
    const valorLimpo = value.trim().replace(/\s/g, '');
    const apenasDigitos = valorLimpo.replace(/\D/g, '');

    if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) return 'email';
    if (apenasDigitos.length === 14) return 'cnpj';
    if (apenasDigitos.length === 11) {
        if (/[\(\)]/.test(value) || /\d{4,5}-\d{4}/.test(value)) return 'telefone';
        return 'cpf';
    }
    if (/^[a-zA-ZÀ-ÿ\s]+$/.test(value) && value.trim().includes(' ')) return 'nome';
    return 'desconhecido';
}

export function maskLgpd(
    value: string,
    tipo: 'cpf' | 'cnpj' | 'nome' | 'email'
): string {
    if (!value) return value;
    switch (tipo) {
        case 'cpf':
            return maskCpf(value);
        case 'cnpj':
            return maskCnpj(value);
        case 'nome':
            return maskNome(value);
        case 'email':
            return maskEmail(value);
        default:
            return value;
    }
}

export function maskLgpdGenerico(value: string): string {
    if (!shouldMask() || !value) return value;
    switch (detectarTipo(value)) {
        case 'cpf':
            return maskCpf(value);
        case 'cnpj':
            return maskCnpj(value);
        case 'email':
            return maskEmail(value);
        case 'nome':
            return maskNome(value);
        default:
            return maskTextoGenerico(value);
    }
}

function maskCep(value: string): string {
    return value ? `*****-${value.slice(-3)}` : '';
}

function maskPhone(value: string): string {
    return value ? `(**)*****-${value.slice(-4)}` : '';
}

function maskCustom(
    value: string,
    posicao: 'left' | 'right' | 'center' = 'center',
    tamanho = 3
): string {
    if (!value) {
        return '';
    }
    const maskedPart = '*'.repeat(tamanho);
    switch (posicao) {
        case 'left':
            return maskedPart + value.slice(tamanho);
        case 'right':
            return value.slice(0, Math.max(0, value.length - tamanho)) + maskedPart;
        case 'center': {
            const meio = Math.floor(value.length / 2);
            const inicio = Math.max(0, meio - Math.floor(tamanho / 2));
            return value.slice(0, inicio) + maskedPart + value.slice(inicio + tamanho);
        }
        default:
            return maskedPart;
    }
}

function maskGenericField(value: string): string {
    if (!value) {
        return '';
    }
    return '*'.repeat(Math.min(value.length, 18));
}

export function maskLgpdCampo(
    value: string,
    tipo: '' | 'nome' | 'cep' | 'phone' | 'custom' = '',
    options?: { posicao?: 'left' | 'right' | 'center'; tamanho?: number }
): string {
    if (!shouldMask() || !value) {
        return value;
    }
    switch (tipo) {
        case 'nome':
            return maskNome(value);
        case 'cep':
            return maskCep(value);
        case 'phone':
            return maskPhone(value);
        case 'custom':
            return maskCustom(value, options?.posicao, options?.tamanho ?? 3);
        default:
            return maskGenericField(value);
    }
}
